import { IpAddress, IpVersion } from "./IpAddress";
import { MacAddress } from "./MacAddress";
import { IpSubnet } from "./IpSubnet";
import { Dictionary } from "../Utils/Dictionary";
import { VirtualHostInfo } from "../InfoSets/VirtualHostInfo";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

export class VirtualNic implements ObjectSerializer {

	public Key: string;
	public get HasKey(): boolean {
		return !!this.Key;
	}

	public HostID: string;
	public get HasHostID(): boolean {
		return !!this.HostID;
	}

	public Device: string;
	public get HasDevice(): boolean {
		return !!this.Device;
	}

	public Port: string;
	public get HasPort(): boolean {
		return !!this.Port;
	}

	public PortGroup: string;
	public get HasPortGroup(): boolean {
		return !!this.PortGroup;
	}

	public IpAddress: IpAddress;
	public get HasIpAddress(): boolean {
		return this.IpAddress && this.IpAddress.IsValidDiscoveryAddress;
	}

	public SubnetMask: IpAddress;
	public get HasSubnetMask(): boolean {
		return !!this.SubnetMask;
	}
		
	public MacAddress: MacAddress;
	public get HasMacAddress(): boolean {
		return this.MacAddress && !this.MacAddress.IsNull;
	}

	public IsDhcp: boolean;

	public get IpSubnet(): IpSubnet {
		var ipSubnet: IpSubnet = null;

		if (this.IpAddress.IpVersion == IpVersion.IPv4) {
			if (this.HasSubnetMask)
				ipSubnet = IpSubnet.CreateFromAddresses(this.IpAddress, this.SubnetMask);
		}

		return ipSubnet;
	}

	// DVS - Distributed Virtual Switch
	public DvsPortConnection_PortGroupKey: string;
	public get HasDvsPortConnection_PortGroupKey(): boolean {
		return !!this.DvsPortConnection_PortGroupKey;
	}

	public DvsPortConnection_PortKey: string;
	public get HasDvsPortConnection_PortKey(): boolean {
		return !!this.DvsPortConnection_PortKey;
	}

	public DvsPortConnection_SwitchUUID: string;
	public get HasDvsPortConnection_SwitchUUID(): boolean {
		return !!this.DvsPortConnection_SwitchUUID;
	}

	constructor(key?:string) {
		this.Key = key;

		this.Init();
	}

	private Init():void	{
		this.HostID = '';
		this.Device = '';
		this.Port = '';
		this.PortGroup = '';

		this.IpAddress = null;
		this.SubnetMask = null;
		this.MacAddress = null;

		this.IsDhcp = false;

		this.DvsPortConnection_PortGroupKey = '';
		this.DvsPortConnection_PortKey = '';
		this.DvsPortConnection_SwitchUUID = '';
	}

	public Write(obj: any, context: any): void {
		obj.virtual_nic_entry = {};
		obj = obj.virtual_nic_entry;

		obj.key = this.Key;

		if (this.HasHostID)
			obj.host_id = this.HostID;

		if (this.HasDevice)
			obj.vnic_device = this.Device;

		if (this.HasPort)
			obj.vnic_port = this.Port;

		if (this.HasPortGroup)
			obj.vnic_port_group = this.PortGroup;

		if (this.HasIpAddress)
			obj.ip_address = ValueEncoder.EncodeIpAddress(this.IpAddress);

		if (this.HasSubnetMask)
			obj.subnet_mask = ValueEncoder.EncodeIpAddress(this.SubnetMask);

		if (this.HasMacAddress)
			obj.mac_address = ValueEncoder.EncodeMacAddress(this.MacAddress);

		obj.dhcp = ValueEncoder.EncodeBoolean(this.IsDhcp);

		// DVS
		if (this.HasDvsPortConnection_SwitchUUID)
			obj.vnic_dvs_switch_uuid = this.DvsPortConnection_SwitchUUID;
		if (this.HasDvsPortConnection_PortGroupKey)
			obj.vnic_dvs_port_group = this.DvsPortConnection_PortGroupKey;
		if (this.HasDvsPortConnection_PortKey)
			obj.vnic_dvs_port = this.DvsPortConnection_PortKey;
	}

	public Read(obj: any, context: any): void {
		if (!obj.virtual_nic_entry)
			throw new Error();
		obj = obj.virtual_nic_entry;

		if (obj.key !== undefined)
			this.Key = obj.key;

		if (obj.host_id !== undefined)
			this.HostID = obj.host_id;

		if (obj.vnic_device !== undefined)
			this.Device = obj.vnic_device;

		if (obj.vnic_port !== undefined)
			this.Port = obj.vnic_port;

		if (obj.vnic_port_group !== undefined)
			this.PortGroup = obj.vnic_port_group;

		if (obj.ip_address !== undefined)
			this.IpAddress = ValueEncoder.DecodeIpAddress(obj.ip_address);

		if (obj.subnet_mask !== undefined)
			this.SubnetMask = ValueEncoder.DecodeIpAddress(obj.subnet_mask);

		if (obj.mac_address !== undefined)
			this.MacAddress = ValueEncoder.DecodeMacAddress(obj.mac_address);

		if (obj.dhcp !== undefined)
			this.IsDhcp = ValueEncoder.DecodeBoolean(obj.dhcp);

		if (obj.vnic_dvs_switch_uuid !== undefined)
			this.DvsPortConnection_SwitchUUID = obj.vnic_dvs_switch_uuid;

		if (obj.vnic_dvs_port_group !== undefined)
			this.DvsPortConnection_PortGroupKey = obj.vnic_dvs_port_group;

		if (obj.vnic_dvs_port !== undefined)
			this.DvsPortConnection_PortKey = obj.vnic_dvs_port;
	}
}

export class VirtualNicTable implements ObjectSerializer {

	private _vnicTable: Dictionary<string, VirtualNic>;

	public VirtualHost: VirtualHostInfo;
	public get HasVirtualHost(): boolean {
		return this.VirtualHost && this.VirtualHost.HasHostID;
	}

	constructor(parentHost: VirtualHostInfo)
	{
		this._vnicTable = new Dictionary<string, VirtualNic>();
		this.VirtualHost = parentHost;
	}

	public Add(vnicKey: string): VirtualNic {
		var vNic: VirtualNic = null;

		if (this._vnicTable.ContainsKey(vnicKey)) {
			vNic = this._vnicTable.Item(vnicKey);
		}
		else {
			vNic = new VirtualNic(vnicKey);
			this._vnicTable.Add(vnicKey, vNic);
		}

		if (this.HasVirtualHost)
			vNic.HostID = this.VirtualHost.HostID;

		return vNic;
	}

	public AddNic(virtNic: VirtualNic):void {
		if (virtNic == null)
			return;
		if (!virtNic.HasKey)
			return;

		this._vnicTable.Remove(virtNic.Key);
			
		this._vnicTable.Add(virtNic.Key, virtNic);

		if (this.HasVirtualHost)
			virtNic.HostID = this.VirtualHost.HostID;
	}

	public Clear():void	{
		this._vnicTable.Clear();
	}

	public get Count():number {
		return this._vnicTable.Count;
	}

	public Remove(vnicKey: string): boolean {
		return this._vnicTable.Remove(vnicKey);
	}

	public ForEach(callbackfn: (value: VirtualNic, index: number, array: VirtualNic[]) => void, thisArg?: any): void {
		this._vnicTable.Values().ForEach(callbackfn, thisArg);
	}

	public Some(callbackfn: (value: VirtualNic, index: number, array: VirtualNic[]) => boolean, thisArg?: any): boolean {
		return this._vnicTable.Values().Some(callbackfn, thisArg);
	}

	public Every(callbackfn: (value: VirtualNic, index: number, array: VirtualNic[]) => boolean, thisArg?: any): boolean {
		return this._vnicTable.Values().Every(callbackfn, thisArg);
	}

	public Write(obj: any, context: any): void {
		obj.virtual_nic_table = {};
		obj = obj.virtual_nic_table;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._vnicTable.Values(), context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.virtual_nic_table)
			throw new Error();
		obj = obj.virtual_nic_table;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let entry = new VirtualNic();
				entry.Read(value, context);
				this.AddNic(entry);
			}, this);
		}
	}
}
